import React, { useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  DownloadSimple,
  FileXls,
  FileText,
  ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const PERIOD_DEFAULT = () => new Date().toISOString().slice(0, 7);

function downloadJSON(obj, filename) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadExcel(rows, filename, sheetName = "Sheet1") {
  if (!rows || rows.length === 0) {
    toast.error("Nothing to export");
    return;
  }
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

const Stat = ({ label, value, accent }) => (
  <div className="rounded-lg border border-border bg-card p-4">
    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </div>
    <div
      className={`font-display text-xl mt-1 tabular-nums ${accent ? "text-gold" : ""}`}
    >
      {value}
    </div>
  </div>
);

const ExportBar = ({ onExcel, onJSON, label }) => (
  <div className="flex items-center gap-2">
    <Button
      data-testid={`export-excel-${label}`}
      variant="ghost"
      onClick={onExcel}
      className="text-gold"
    >
      <FileXls size={14} className="mr-1" /> Excel
    </Button>
    <Button
      data-testid={`export-json-${label}`}
      variant="ghost"
      onClick={onJSON}
      className="text-gold"
    >
      <FileText size={14} className="mr-1" /> JSON
    </Button>
  </div>
);

function SalesRegister({ period }) {
  const [data, setData] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get(`/gst/sales-register?period=${period}`);
      setData(data);
    } catch (e) {
      toast.error("Failed to load");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [period]);
  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {data.totals.count} invoices · {period}
        </div>
        <ExportBar
          label="sales-reg"
          onExcel={() =>
            downloadExcel(
              data.rows,
              `gst-sales-register-${period}.xlsx`,
              "Sales Register",
            )
          }
          onJSON={() => downloadJSON(data, `gst-sales-register-${period}.json`)}
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Taxable" value={inr(data.totals.taxable_value)} />
        <Stat label="CGST" value={inr(data.totals.cgst)} />
        <Stat label="SGST" value={inr(data.totals.sgst)} />
        <Stat label="IGST" value={inr(data.totals.igst)} />
        <Stat label="Grand" value={inr(data.totals.total)} accent />
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Invoice</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead className="text-right">Taxable</TableHead>
              <TableHead className="text-right">CGST</TableHead>
              <TableHead className="text-right">SGST</TableHead>
              <TableHead className="text-right">IGST</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-8 text-muted-foreground"
                >
                  No sales in period.
                </TableCell>
              </TableRow>
            ) : (
              data.rows.map((r) => (
                <TableRow key={r.invoice_no}>
                  <TableCell className="font-mono text-xs">
                    {r.invoice_no}
                  </TableCell>
                  <TableCell className="text-sm">{r.invoice_date}</TableCell>
                  <TableCell>{r.customer_name}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.customer_gstin || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.taxable_value)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.cgst)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.sgst)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.igst)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.total)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function PurchaseRegister({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api
      .get(`/gst/purchase-register?period=${period}`)
      .then((r) => setData(r.data));
  }, [period]);
  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {data.totals.count} purchases · {period}
        </div>
        <ExportBar
          label="purchase-reg"
          onExcel={() =>
            downloadExcel(
              data.rows,
              `gst-purchase-register-${period}.xlsx`,
              "Purchase Register",
            )
          }
          onJSON={() =>
            downloadJSON(data, `gst-purchase-register-${period}.json`)
          }
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Taxable" value={inr(data.totals.taxable_value)} />
        <Stat label="CGST" value={inr(data.totals.cgst)} />
        <Stat label="SGST" value={inr(data.totals.sgst)} />
        <Stat label="IGST" value={inr(data.totals.igst)} />
        <Stat label="Grand" value={inr(data.totals.total)} accent />
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Invoice</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>GSTIN</TableHead>
              <TableHead className="text-right">Taxable</TableHead>
              <TableHead className="text-right">CGST</TableHead>
              <TableHead className="text-right">SGST</TableHead>
              <TableHead className="text-right">IGST</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-8 text-muted-foreground"
                >
                  No purchases in period.
                </TableCell>
              </TableRow>
            ) : (
              data.rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell className="font-mono text-xs">
                    {r.invoice_no}
                  </TableCell>
                  <TableCell className="text-sm">{r.invoice_date}</TableCell>
                  <TableCell>{r.supplier_name}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {r.supplier_gstin || "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.taxable_value)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.cgst)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.sgst)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.igst)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.total)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function HSNSummary({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/gst/hsn-summary?period=${period}`).then((r) => setData(r.data));
  }, [period]);
  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {data.rows.length} HSN groups · {period}
        </div>
        <ExportBar
          label="hsn"
          onExcel={() =>
            downloadExcel(data.rows, `gst-hsn-${period}.xlsx`, "HSN Summary")
          }
          onJSON={() => downloadJSON(data, `gst-hsn-${period}.json`)}
        />
      </div>
      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>HSN</TableHead>
              <TableHead>UQC</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Weight (g)</TableHead>
              <TableHead className="text-right">Rate %</TableHead>
              <TableHead className="text-right">Taxable</TableHead>
              <TableHead className="text-right">GST</TableHead>
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="text-center py-8 text-muted-foreground"
                >
                  No data.
                </TableCell>
              </TableRow>
            ) : (
              data.rows.map((r) => (
                <TableRow key={r.hsn_code}>
                  <TableCell className="font-mono">{r.hsn_code}</TableCell>
                  <TableCell>{r.uqc}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.total_quantity}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {(r.total_weight || 0).toFixed(3)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {r.rate}%
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.taxable_value)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.gst_amount)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(r.total_value)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function GSTR1({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/gst/gstr1?period=${period}`).then((r) => setData(r.data));
  }, [period]);
  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <ExportBar
          label="gstr1"
          onExcel={() => {
            const sheets = {
              B2B: data.b2b.rows,
              "B2C-Large": data.b2cl.rows,
              "B2C-Small": data.b2cs.rows,
              HSN: data.hsn,
            };
            const wb = XLSX.utils.book_new();
            Object.entries(sheets).forEach(([name, rows]) => {
              const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{}]);
              XLSX.utils.book_append_sheet(wb, ws, name);
            });
            XLSX.writeFile(wb, `gstr1-${period}.xlsx`);
          }}
          onJSON={() => downloadJSON(data, `gstr1-${period}.json`)}
        />
      </div>

      {[
        { key: "b2b", label: "B2B - Sales to Registered Customers" },
        { key: "b2cl", label: "B2CL - Large Sales to Unregistered (>₹2.5L)" },
        { key: "b2cs", label: "B2CS - Small Sales to Unregistered" },
      ].map(({ key, label }) => {
        const sec = data[key];
        return (
          <section key={key} className="space-y-3">
            <h3 className="font-display text-lg">
              {label}{" "}
              <span className="text-xs text-muted-foreground">
                ({sec.totals.count} invoices)
              </span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Taxable" value={inr(sec.totals.taxable_value)} />
              <Stat label="CGST" value={inr(sec.totals.cgst)} />
              <Stat label="SGST" value={inr(sec.totals.sgst)} />
              <Stat label="IGST" value={inr(sec.totals.igst)} />
              <Stat label="Total" value={inr(sec.totals.total)} accent />
            </div>
          </section>
        );
      })}
    </div>
  );
}

function GSTR3B({ period }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/gst/gstr3b?period=${period}`).then((r) => setData(r.data));
  }, [period]);
  if (!data) return <div className="text-muted-foreground">Loading...</div>;
  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <ExportBar
          label="gstr3b"
          onExcel={() => {
            const flat = [
              { section: "Outward Supplies", ...data.outward_supplies },
              { section: "ITC Available", ...data.itc_available },
              {
                section: "Net Tax Payable",
                taxable_value: 0,
                ...data.net_tax_payable,
              },
            ];
            downloadExcel(flat, `gstr3b-${period}.xlsx`, "GSTR-3B");
          }}
          onJSON={() => downloadJSON(data, `gstr3b-${period}.json`)}
        />
      </div>
      <Section title="3.1 Outward Supplies" data={data.outward_supplies} />
      <Section
        title="4. Eligible ITC (Input Tax Credit)"
        data={data.itc_available}
      />
      <Section
        title="Net Tax Payable (Sales − ITC)"
        data={data.net_tax_payable}
        accent
      />
    </div>
  );
}

const Section = ({ title, data, accent }) => (
  <section className="space-y-3">
    <h3 className="font-display text-lg">{title}</h3>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {"taxable_value" in data && (
        <Stat label="Taxable" value={inr(data.taxable_value)} />
      )}
      <Stat label="CGST" value={inr(data.cgst)} accent={accent} />
      <Stat label="SGST" value={inr(data.sgst)} accent={accent} />
      <Stat label="IGST" value={inr(data.igst)} accent={accent} />
    </div>
  </section>
);

export default function GST() {
  const [period, setPeriod] = useState(PERIOD_DEFAULT());
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="gst-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Compliance
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            GST Returns
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            GSTR-1, GSTR-3B, HSN summary and registers. Export to Excel or JSON.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
              Period
            </Label>
            <Input
              data-testid="gst-period"
              type="month"
              className="mt-1.5 bg-card border-border w-44"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            />
          </div>
          <Button
            onClick={() => setReloadKey((k) => k + 1)}
            variant="ghost"
            className="text-gold mb-0.5"
          >
            <ArrowsClockwise size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </header>

      <Tabs defaultValue="gstr1" key={reloadKey} className="space-y-6">
        <TabsList className="bg-secondary">
          <TabsTrigger value="gstr1" data-testid="gst-tab-gstr1">
            GSTR-1
          </TabsTrigger>
          <TabsTrigger value="gstr3b" data-testid="gst-tab-gstr3b">
            GSTR-3B
          </TabsTrigger>
          <TabsTrigger value="hsn" data-testid="gst-tab-hsn">
            HSN Summary
          </TabsTrigger>
          <TabsTrigger value="sales-reg" data-testid="gst-tab-sales">
            Sales Register
          </TabsTrigger>
          <TabsTrigger value="purchase-reg" data-testid="gst-tab-purchase">
            Purchase Register
          </TabsTrigger>
        </TabsList>
        <TabsContent value="gstr1">
          <GSTR1 period={period} />
        </TabsContent>
        <TabsContent value="gstr3b">
          <GSTR3B period={period} />
        </TabsContent>
        <TabsContent value="hsn">
          <HSNSummary period={period} />
        </TabsContent>
        <TabsContent value="sales-reg">
          <SalesRegister period={period} />
        </TabsContent>
        <TabsContent value="purchase-reg">
          <PurchaseRegister period={period} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
